import { useState, useEffect } from 'react';
import { api, formatApiError } from '../lib/api';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Bot, CreditCard, Loader2, RotateCcw, Power, PowerOff, ChevronLeft, ChevronRight, TrendingUp, Users, Zap } from 'lucide-react';

export default function AdminSubscriptionsPage() {
  const [subs, setSubs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [planFilter, setPlanFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [aiData, setAiData] = useState(null);

  useEffect(() => { load(); }, [page, planFilter]);

  const load = async () => {
    setLoading(true);
    try {
      const [s, ai] = await Promise.all([
        api.adminSubscriptions({ page, limit: 20, plan: planFilter !== 'all' ? planFilter : undefined }),
        api.adminAIMonitoring(),
      ]);
      setSubs(s.subscriptions || []);
      setTotal(s.total || 0);
      setAiData(ai);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const handleResetAI = async (userId) => {
    try {
      await api.adminResetAICounter(userId);
      toast.success('Contador IA reseteado');
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleToggleAI = async (userId, enabled) => {
    try {
      await api.adminToggleAI(userId, { enabled });
      toast.success(enabled ? 'IA activada' : 'IA desactivada');
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleChangePlan = async (userId, plan) => {
    try {
      await api.adminChangePlan(userId, { plan_id: plan });
      toast.success(`Plan cambiado a ${plan}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-6 lg:p-8 max-w-[1400px] mx-auto" data-testid="admin-subscriptions-page">
      <h1 className="text-xl font-semibold text-zinc-100 mb-1">Suscripciones & IA</h1>
      <p className="text-xs text-zinc-600 mb-6">{total} suscriptores</p>

      {/* AI Summary Cards */}
      {aiData && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <SummaryCard label="Usuarios con IA" value={aiData.total_ai_users} icon={Bot} color="text-cyan-400" />
          <SummaryCard label="Mensajes este mes" value={aiData.total_responses_month} icon={Zap} color="text-amber-400" />
          <SummaryCard label="Cerca del limite" value={aiData.near_limit_count} icon={TrendingUp} color={aiData.near_limit_count > 0 ? 'text-red-400' : 'text-emerald-400'} />
          <SummaryCard label="Costo estimado" value={`$${aiData.estimated_cost_month} USD`} icon={CreditCard} color="text-purple-400" />
        </div>
      )}

      {/* Filter */}
      <div className="flex gap-3 mb-6">
        <Select value={planFilter} onValueChange={v => { setPlanFilter(v); setPage(1); }}>
          <SelectTrigger className="w-40 bg-white/[0.03] border-white/5 text-zinc-300 h-9 text-sm"><SelectValue placeholder="Plan" /></SelectTrigger>
          <SelectContent className="bg-[#121215] border-white/10">
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="free">Free</SelectItem>
            <SelectItem value="basic">Basic</SelectItem>
            <SelectItem value="premium">Premium</SelectItem>
            <SelectItem value="elite">Elite</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-white/[0.04] bg-[#0A0A0D] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/[0.04]">
                <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider font-medium">Usuario</th>
                <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider font-medium">Plan</th>
                <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider font-medium">IA Tier</th>
                <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider font-medium">Uso IA</th>
                <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider font-medium">Billing</th>
                <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider font-medium">Vence</th>
                <th className="text-right px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider font-medium">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="text-center py-12"><Loader2 className="w-5 h-5 text-zinc-600 animate-spin mx-auto" /></td></tr>
              ) : subs.map(s => {
                const aiUser = aiData?.users?.find(u => u.user_id === s.user_id);
                const usagePct = aiUser?.usage_pct || 0;
                return (
                  <tr key={s.user_id} className="border-b border-white/[0.02] hover:bg-white/[0.01]" data-testid={`sub-row-${s.user_id}`}>
                    <td className="px-4 py-3">
                      <p className="text-xs text-zinc-200 font-medium">{s.name}</p>
                      <p className="text-[10px] text-zinc-600">{s.email}</p>
                    </td>
                    <td className="px-4 py-3">
                      <Select defaultValue={s.subscription || 'free'} onValueChange={v => handleChangePlan(s.user_id, v)}>
                        <SelectTrigger className="w-24 h-7 text-[10px] bg-transparent border-white/5"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#121215] border-white/10">
                          <SelectItem value="free">Free</SelectItem>
                          <SelectItem value="basic">Basic</SelectItem>
                          <SelectItem value="premium">Premium</SelectItem>
                          <SelectItem value="elite">Elite</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3">
                      {s.ai_features?.enabled ? (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-cyan-500/10 text-cyan-400">{s.ai_features.tier}</span>
                      ) : <span className="text-[10px] text-zinc-700">-</span>}
                    </td>
                    <td className="px-4 py-3">
                      {aiUser ? (
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-1.5 rounded-full bg-zinc-800">
                            <div className={`h-full rounded-full ${usagePct >= 80 ? 'bg-red-400' : usagePct >= 50 ? 'bg-amber-400' : 'bg-emerald-400'}`}
                              style={{ width: `${Math.min(usagePct, 100)}%` }} />
                          </div>
                          <span className="text-[10px] text-zinc-500">{aiUser.responses_this_month}/{aiUser.max_monthly}</span>
                        </div>
                      ) : <span className="text-[10px] text-zinc-700">-</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] ${s.billing_status === 'payment_failed' ? 'text-red-400' : s.billing_status === 'active' ? 'text-emerald-400' : 'text-zinc-500'}`}>
                        {s.billing_status || '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-[10px] text-zinc-500">{s.period_end ? new Date(s.period_end).toLocaleDateString('es-MX') : '-'}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center gap-1 justify-end">
                        {aiUser && (
                          <>
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-amber-400 hover:bg-amber-500/10"
                              onClick={() => handleResetAI(s.user_id)} title="Reset contador IA">
                              <RotateCcw className="w-3.5 h-3.5" />
                            </Button>
                            <Button size="sm" variant="ghost"
                              className={`h-7 w-7 p-0 ${aiUser.enabled ? 'text-red-400 hover:bg-red-500/10' : 'text-emerald-400 hover:bg-emerald-500/10'}`}
                              onClick={() => handleToggleAI(s.user_id, !aiUser.enabled)}
                              title={aiUser.enabled ? 'Desactivar IA' : 'Activar IA'}>
                              {aiUser.enabled ? <PowerOff className="w-3.5 h-3.5" /> : <Power className="w-3.5 h-3.5" />}
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-white/[0.04]">
          <span className="text-xs text-zinc-600">Pagina {page}</span>
          <div className="flex gap-1">
            <Button size="sm" variant="ghost" disabled={page === 1} onClick={() => setPage(p => p - 1)} className="h-7 text-xs text-zinc-400"><ChevronLeft className="w-3 h-3" /></Button>
            <Button size="sm" variant="ghost" disabled={subs.length < 20} onClick={() => setPage(p => p + 1)} className="h-7 text-xs text-zinc-400"><ChevronRight className="w-3 h-3" /></Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ label, value, icon: Icon, color }) {
  return (
    <div className="rounded-xl border border-white/[0.04] bg-[#0A0A0D] p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${color}`} />
        <span className="text-[10px] text-zinc-600 uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-lg font-semibold text-zinc-100">{value ?? '-'}</p>
    </div>
  );
}
